import { Add, Delete } from "@mui/icons-material";
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import MoneyInput from "../../components/MoneyInput";
import PhoneInput from "../../components/PhoneInput";
import StateSelectInput from "../../components/StateSelectInput";
import ZipCodeInput from "../../components/ZipCodeInput";
import { formatCurrencyInCents, formatPhone, formatZipCode, parseMoneyToCents } from "../../helpers/formatters";
import { applyName } from '../../helpers/captureMode';
import { quoteEditable } from '../../helpers/quotes';

function emptyShippingOption() {
    return { name: '', cost: '', delivery_days: '' };
}

function emptyPaymentOption() {
    return { name: '', installments: 1, amount: '' };
}

function centsToMoneyInput(cents) {
    const value = Number(cents) || 0;
    return (value / 100).toFixed(2);
}

function normalizeValidUntil(raw) {
    if (!raw) {
        return '';
    }
    const str = String(raw).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
        return str.slice(0, 10);
    }
    const date = new Date(str);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toISOString().slice(0, 10);
}

function QuoteForm({ mode = 'create', quoteId, initialData }) {
    const navigate = useNavigate();
    const [customerId, setCustomerId] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [address, setAddress] = useState('');
    const [addressNumber, setAddressNumber] = useState('');
    const [addressComplement, setAddressComplement] = useState('');
    const [neighborhood, setNeighborhood] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [productName, setProductName] = useState('');
    const [productUnitPrice, setProductUnitPrice] = useState('');
    const [productQuantity, setProductQuantity] = useState(1);
    const [productId, setProductId] = useState('');
    const [products, setProducts] = useState([]);
    const [discount, setDiscount] = useState('');
    const [discountReason, setDiscountReason] = useState('');
    const [productionDays, setProductionDays] = useState('');
    const [validUntil, setValidUntil] = useState('');
    const [shippingOptions, setShippingOptions] = useState([]);
    const [paymentOptions, setPaymentOptions] = useState([]);
    const [observations, setObservations] = useState('');
    const [amount, setAmount] = useState(0);
    const [status, setStatus] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resultErrors, setResultErrors] = useState({ has_errors: false });
    const [isLoadingCustomerSearch, setIsLoadingCustomerSearch] = useState(false);
    const [isLoadingProductSearch, setIsLoadingProductSearch] = useState(false);
    const [customerResults, setCustomerResults] = useState([]);
    const [productResults, setProductResults] = useState([]);
    const [anchorElCustomer, setAnchorElCustomer] = useState(null);
    const [anchorElProduct, setAnchorElProduct] = useState(null);
    const [addProductErrors, setAddProductErrors] = useState({
        product_name: '',
        product_price: '',
        product_quantity: '',
    });
    const customerTimerRef = useRef(null);
    const productTimerRef = useRef(null);
    const initializedRef = useRef(false);

    const readOnly = mode === 'edit' && !quoteEditable(status);

    const recomputeAmount = (nextProducts, nextDiscount) => {
        const productsTotal = (nextProducts || []).reduce(
            (sum, product) => sum + (Number(product.total) || ((Number(product.base_price) || 0) * (Number(product.quantity) || 0))),
            0,
        );
        const discountCents = parseMoneyToCents(nextDiscount);
        setAmount(Math.max(0, productsTotal - discountCents));
    };

    const applyCustomerAddress = (customer = {}) => {
        setZipCode(formatZipCode(customer.zip_code || ''));
        setAddress(customer.address || '');
        setAddressNumber(customer.address_number || '');
        setAddressComplement(customer.address_complement || '');
        setNeighborhood(customer.neighborhood || '');
        setCity(customer.city || '');
        setState(customer.state || '');
    };

    const loadCustomerAddress = (customerUuid) => {
        if (!customerUuid) {
            return;
        }
        api.get(`customers/${customerUuid}`)
            .then((response) => {
                const customer = response.data?.data || response.data || {};
                applyCustomerAddress(customer);
            })
            .catch(() => {
                // Mantém os campos vazios se o cliente não puder ser carregado.
            });
    };

    useEffect(() => {
        if (mode !== 'edit' || !initialData || initializedRef.current) {
            return;
        }
        initializedRef.current = true;
        const loadedCustomerId = initialData.customer_uuid || initialData.customer?.id || initialData.customer?.uuid || '';
        setCustomerId(loadedCustomerId);
        setCustomerName(initialData.customer_name || initialData.customer?.name || '');
        setCustomerEmail(initialData.customer_email || initialData.customer?.email || '');
        setCustomerPhone(formatPhone(initialData.customer_phone || initialData.customer?.phone || ''));
        if (initialData.customer) {
            applyCustomerAddress(initialData.customer);
        } else if (loadedCustomerId) {
            loadCustomerAddress(loadedCustomerId);
        }
        setStatus(initialData.status || '');
        setProductionDays(
            initialData.production_days === 0 || initialData.production_days
                ? String(initialData.production_days)
                : '',
        );
        setValidUntil(normalizeValidUntil(initialData.valid_until));
        setObservations(initialData.observations || '');
        setDiscount(centsToMoneyInput(initialData.discount || 0));
        setDiscountReason(initialData.discount_reason || '');

        const loadedProducts = Array.isArray(initialData.products)
            ? initialData.products.map((product) => {
                const basePrice = Number(product.base_price) || 0;
                const quantity = Number(product.quantity) || 0;
                return {
                    ...product,
                    id: product.id || product.uuid || '',
                    base_price: basePrice,
                    quantity,
                    total: basePrice * quantity,
                };
            })
            : [];
        setProducts(loadedProducts);

        setShippingOptions(
            Array.isArray(initialData.shipping_options)
                ? initialData.shipping_options.map((opt) => ({
                    name: opt.name || '',
                    cost: centsToMoneyInput(opt.cost || 0),
                    delivery_days: opt.delivery_days === 0 || opt.delivery_days
                        ? String(opt.delivery_days)
                        : '',
                }))
                : [],
        );

        setPaymentOptions(
            Array.isArray(initialData.payment_options)
                ? initialData.payment_options.map((opt) => ({
                    name: opt.name || '',
                    installments: Number(opt.installments) || 1,
                    amount: centsToMoneyInput(opt.amount || 0),
                }))
                : [],
        );

        recomputeAmount(loadedProducts, centsToMoneyInput(initialData.discount || 0));
    }, [mode, initialData]);

    useEffect(() => {
        recomputeAmount(products, discount);
    }, [products, discount]);

    const handleAddProduct = () => {
        const unitPriceInCents = parseMoneyToCents(productUnitPrice);
        const currentQuantity = parseInt(productQuantity, 10);
        let hasProductErrors = false;
        const currentProductErrors = {
            product_name: '',
            product_price: '',
            product_quantity: '',
        };

        if (productName === '' && productId === '') {
            currentProductErrors.product_name = 'Informe o nome do produto';
            hasProductErrors = true;
        }
        if (isNaN(unitPriceInCents) || unitPriceInCents <= 0) {
            currentProductErrors.product_price = 'Informe o valor do produto';
            hasProductErrors = true;
        }
        if (isNaN(currentQuantity) || currentQuantity <= 0) {
            currentProductErrors.product_quantity = 'Deve ser informada a quantidade';
            hasProductErrors = true;
        }

        if (hasProductErrors) {
            setResultErrors((prev) => ({ ...prev, has_errors: true }));
            setAddProductErrors(currentProductErrors);
            return;
        }

        setAddProductErrors({
            product_name: '',
            product_price: '',
            product_quantity: '',
        });
        setResultErrors((prev) => ({ ...prev, has_errors: false, products: null }));

        const product = {
            name: productName,
            base_price: unitPriceInCents,
            quantity: currentQuantity,
            total: unitPriceInCents * currentQuantity,
        };
        if (productId !== '') {
            product.id = productId;
            setProductId('');
        }

        setProducts([...products, product]);
        setProductName('');
        setProductUnitPrice('');
        setProductQuantity(1);
    };

    const handleRemoveProduct = (index) => {
        setProducts(products.filter((_, i) => i !== index));
    };

    const customerSearch = (name) => {
        if (name.length >= 3) {
            if (customerTimerRef.current) {
                clearTimeout(customerTimerRef.current);
            }
            customerTimerRef.current = setTimeout(() => {
                setIsLoadingCustomerSearch(true);
                api.get(`customers?q=${encodeURIComponent(name)}`)
                    .then((response) => {
                        const d = response.data;
                        const rows = Array.isArray(d?.customers)
                            ? d.customers
                            : Array.isArray(d?.data)
                                ? d.data
                                : [];
                        setCustomerResults(rows);
                    })
                    .catch(() => {
                        setCustomerResults([]);
                    })
                    .finally(() => {
                        setIsLoadingCustomerSearch(false);
                    });
            }, 1000);
        } else {
            setCustomerResults([]);
        }
    };

    const productSearch = (name) => {
        if (name.length >= 3) {
            if (productTimerRef.current) {
                clearTimeout(productTimerRef.current);
            }
            productTimerRef.current = setTimeout(() => {
                setIsLoadingProductSearch(true);
                api.get(`products?q=${encodeURIComponent(name)}`)
                    .then((response) => {
                        const d = response.data;
                        const rows = Array.isArray(d?.products)
                            ? d.products
                            : Array.isArray(d?.data)
                                ? d.data
                                : [];
                        setProductResults(rows);
                    })
                    .catch(() => {
                        setProductResults([]);
                    })
                    .finally(() => {
                        setIsLoadingProductSearch(false);
                    });
            }, 1000);
        } else {
            setProductResults([]);
        }
    };

    const handleCloseCustomerMenu = () => {
        setAnchorElCustomer(null);
        setCustomerResults([]);
    };

    const handleCloseProductMenu = () => {
        setAnchorElProduct(null);
        setProductResults([]);
    };

    const updateShippingOption = (index, field, value) => {
        setShippingOptions((prev) => prev.map((item, i) => (
            i === index ? { ...item, [field]: value } : item
        )));
    };

    const updatePaymentOption = (index, field, value) => {
        setPaymentOptions((prev) => prev.map((item, i) => (
            i === index ? { ...item, [field]: value } : item
        )));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (readOnly || isSubmitting) {
            return;
        }

        const invalidData = {};
        if (products.length === 0) {
            invalidData.products = 'Você deve incluir ao menos 1 produto.';
        }
        if (customerId === '' && customerName === '') {
            invalidData.customer_name = 'Informe o nome do cliente';
        }
        const discountCents = parseMoneyToCents(discount);
        if (discountCents > 0 && String(discountReason || '').trim() === '') {
            invalidData.discount_reason = 'Informe o motivo do desconto.';
        }
        if (Object.keys(invalidData).length > 0) {
            setResultErrors({
                ...resultErrors,
                ...invalidData,
                has_errors: true,
            });
            return;
        }

        setIsSubmitting(true);
        const productsTotal = products.reduce(
            (sum, product) => sum + (Number(product.base_price) || 0) * (Number(product.quantity) || 0),
            0,
        );
        const payloadAmount = Math.max(0, productsTotal - discountCents);

        const payload = {
            customer: {
                id: customerId,
                name: customerName,
                phone: customerPhone,
                email: customerEmail,
                address,
                address_number: addressNumber,
                address_complement: addressComplement,
                neighborhood,
                city,
                state,
                zip_code: zipCode,
            },
            products: products.map((product) => {
                const row = {
                    name: product.name,
                    quantity: Number(product.quantity) || 0,
                    base_price: Number(product.base_price) || 0,
                };
                if (product.id) {
                    row.id = product.id;
                }
                return row;
            }),
            amount: payloadAmount,
            discount: discountCents,
            discount_reason: discountCents > 0 ? String(discountReason || '').trim() : '',
            production_days: parseInt(productionDays, 10) || 0,
            valid_until: validUntil || '',
            shipping_options: shippingOptions
                .filter((opt) => String(opt.name || '').trim() !== '')
                .map((opt) => ({
                    name: opt.name,
                    cost: parseMoneyToCents(opt.cost),
                    delivery_days: parseInt(opt.delivery_days, 10) || 0,
                })),
            payment_options: paymentOptions
                .filter((opt) => String(opt.name || '').trim() !== '')
                .map((opt) => ({
                    name: opt.name,
                    installments: parseInt(opt.installments, 10) || 1,
                    amount: parseMoneyToCents(opt.amount),
                })),
            observations: observations || '',
        };

        const request = mode === 'edit'
            ? api.put(`quotes/${quoteId}`, payload)
            : api.post('quotes', payload);

        request
            .then((response) => {
                const data = response.data;
                if (data && data.success === false) {
                    setResultErrors({
                        ...(data.errors || {}),
                        has_errors: true,
                    });
                    return;
                }
                navigate('/admin/quotes');
            })
            .catch((error) => {
                const data = error?.response?.data;
                if (data?.errors) {
                    setResultErrors({
                        ...data.errors,
                        has_errors: true,
                    });
                    return;
                }
                setResultErrors({
                    has_errors: true,
                    form: data?.message || 'Não foi possível salvar o orçamento.',
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const renderFieldError = (field) => {
        if (!resultErrors[field]) {
            return null;
        }
        return (
            <div
                className="text-danger mt-1"
                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
            >
                {resultErrors[field]}
            </div>
        );
    };

    return (
        <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography variant="h5">Dados do cliente</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        id="customer-name"
                        label={isLoadingCustomerSearch ? 'Buscando cliente...' : 'Nome (obrigatório)'}
                        fullWidth
                        value={customerName}
                        disabled={readOnly}
                        onChange={(e) => {
                            setCustomerName(e.target.value);
                            setCustomerId('');
                            customerSearch(e.target.value);
                        }}
                        onFocus={(e) => setAnchorElCustomer(e.currentTarget)}
                    />
                    <Menu
                        anchorEl={anchorElCustomer}
                        open={Boolean(anchorElCustomer) && customerResults.length > 0}
                        onClose={handleCloseCustomerMenu}
                        disableAutoFocusItem
                    >
                        {customerResults.map((customer) => (
                            <MenuItem
                                key={customer.uuid || customer.id}
                                onClick={() => {
                                    setCustomerName(customer.name);
                                    setCustomerEmail(customer.email || '');
                                    setCustomerPhone(formatPhone(customer.phone));
                                    setCustomerId(customer.uuid || customer.id || '');
                                    applyCustomerAddress(customer);
                                    handleCloseCustomerMenu();
                                }}
                            >
                                {applyName(customer.name)}
                            </MenuItem>
                        ))}
                    </Menu>
                    {renderFieldError('customer_name')}
                </Grid>
                <Grid item xs={12} sm={6}>
                    <PhoneInput
                        label="Telefone"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        disabled={readOnly}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="E-mail"
                        fullWidth
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        type="email"
                        disabled={readOnly}
                    />
                    {renderFieldError('customer_email')}
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="h5">Endereço</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <ZipCodeInput
                        zipCode={zipCode}
                        setZipCode={setZipCode}
                        setCity={setCity}
                        setAddress={setAddress}
                        setState={setState}
                        setNeighborhood={setNeighborhood}
                        disabled={readOnly}
                    />
                    {renderFieldError('zip_code')}
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Endereço"
                        fullWidth
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        disabled={readOnly}
                    />
                    {renderFieldError('address')}
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Número"
                        fullWidth
                        value={addressNumber}
                        onChange={(e) => setAddressNumber(e.target.value)}
                        disabled={readOnly}
                    />
                    {renderFieldError('address_number')}
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Complemento"
                        fullWidth
                        value={addressComplement}
                        onChange={(e) => setAddressComplement(e.target.value)}
                        disabled={readOnly}
                    />
                    {renderFieldError('address_complement')}
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Bairro"
                        fullWidth
                        value={neighborhood}
                        onChange={(e) => setNeighborhood(e.target.value)}
                        disabled={readOnly}
                    />
                    {renderFieldError('neighborhood')}
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Cidade"
                        fullWidth
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        disabled={readOnly}
                    />
                    {renderFieldError('city')}
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StateSelectInput
                        state={state}
                        setState={setState}
                        disabled={readOnly}
                    />
                    {renderFieldError('state')}
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="h5">Produtos</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        label={isLoadingProductSearch ? 'Buscando produto...' : 'Nome do produto (obrigatório)'}
                        fullWidth
                        value={productName}
                        disabled={readOnly}
                        onChange={(e) => {
                            setProductName(e.target.value);
                            productSearch(e.target.value);
                        }}
                        onFocus={(e) => setAnchorElProduct(e.currentTarget)}
                    />
                    <Menu
                        anchorEl={anchorElProduct}
                        open={Boolean(anchorElProduct) && productResults.length > 0}
                        onClose={handleCloseProductMenu}
                        disableAutoFocusItem
                    >
                        {productResults.map((product) => (
                            <MenuItem
                                key={product.uuid || product.id}
                                onClick={() => {
                                    setProductName(product.name);
                                    setProductUnitPrice((product.base_price / 100));
                                    setProductQuantity(1);
                                    setProductId(product.uuid || product.id);
                                    handleCloseProductMenu();
                                }}
                            >
                                {product.name} — {formatCurrencyInCents(product.base_price)}
                            </MenuItem>
                        ))}
                    </Menu>
                    {addProductErrors.product_name && (
                        <div className="text-danger mt-1" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                            {addProductErrors.product_name}
                        </div>
                    )}
                </Grid>
                <Grid item xs={12} sm={3}>
                    <MoneyInput
                        label="Valor unitário"
                        value={productUnitPrice}
                        onChange={(newValue) => setProductUnitPrice(newValue)}
                        disabled={readOnly}
                    />
                    {addProductErrors.product_price && (
                        <div className="text-danger mt-1" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                            {addProductErrors.product_price}
                        </div>
                    )}
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        label="Quantidade"
                        fullWidth
                        value={productQuantity}
                        onChange={(e) => setProductQuantity(e.target.value)}
                        inputMode="numeric"
                        disabled={readOnly}
                    />
                    {addProductErrors.product_quantity && (
                        <div className="text-danger mt-1" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                            {addProductErrors.product_quantity}
                        </div>
                    )}
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleAddProduct}
                        startIcon={<Add />}
                        fullWidth
                        disabled={readOnly}
                    >
                        Incluir este produto
                    </Button>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="subtitle1">Produtos adicionados</Typography>
                    <ul>
                        {products.map((product, index) => (
                            <li key={`${product.name}-${index}`}>
                                {product.name} - {product.quantity} x {formatCurrencyInCents(product.base_price)} = {formatCurrencyInCents(product.total || (product.base_price * product.quantity))}
                                {!readOnly && (
                                    <IconButton onClick={() => handleRemoveProduct(index)}>
                                        <Delete color="error" />
                                    </IconButton>
                                )}
                            </li>
                        ))}
                    </ul>
                    {renderFieldError('products')}
                </Grid>

                <Grid item xs={12} sm={4}>
                    <MoneyInput
                        label="Desconto"
                        value={discount}
                        onChange={(newValue) => setDiscount(newValue)}
                        disabled={readOnly}
                    />
                    {renderFieldError('discount')}
                </Grid>
                <Grid item xs={12} sm={8}>
                    <TextField
                        label="Motivo do desconto"
                        fullWidth
                        value={discountReason}
                        onChange={(e) => setDiscountReason(e.target.value)}
                        disabled={readOnly}
                        helperText={parseMoneyToCents(discount) > 0 ? 'Obrigatório quando há desconto' : ' '}
                    />
                    {renderFieldError('discount_reason')}
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Prazo de produção (dias)"
                        fullWidth
                        value={productionDays}
                        onChange={(e) => setProductionDays(e.target.value)}
                        inputMode="numeric"
                        disabled={readOnly}
                    />
                    {renderFieldError('production_days')}
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Validade"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={validUntil}
                        onChange={(e) => setValidUntil(e.target.value)}
                        disabled={readOnly}
                    />
                    {renderFieldError('valid_until')}
                </Grid>

                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h5">Opções de frete</Typography>
                        <Button
                            startIcon={<Add />}
                            onClick={() => setShippingOptions((prev) => [...prev, emptyShippingOption()])}
                            disabled={readOnly}
                        >
                            Adicionar frete
                        </Button>
                    </Box>
                </Grid>
                {shippingOptions.map((option, index) => (
                    <React.Fragment key={`shipping-${index}`}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Nome do frete"
                                fullWidth
                                value={option.name}
                                onChange={(e) => updateShippingOption(index, 'name', e.target.value)}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <MoneyInput
                                label="Custo"
                                value={option.cost}
                                onChange={(newValue) => updateShippingOption(index, 'cost', newValue)}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Prazo (dias)"
                                fullWidth
                                value={option.delivery_days}
                                onChange={(e) => updateShippingOption(index, 'delivery_days', e.target.value)}
                                inputMode="numeric"
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <IconButton
                                onClick={() => setShippingOptions((prev) => prev.filter((_, i) => i !== index))}
                                disabled={readOnly}
                                aria-label="remover frete"
                            >
                                <Delete color="error" />
                            </IconButton>
                        </Grid>
                    </React.Fragment>
                ))}
                {renderFieldError('shipping_options')}

                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h5">Opções de pagamento</Typography>
                        <Button
                            startIcon={<Add />}
                            onClick={() => setPaymentOptions((prev) => [...prev, emptyPaymentOption()])}
                            disabled={readOnly}
                        >
                            Adicionar pagamento
                        </Button>
                    </Box>
                </Grid>
                {paymentOptions.map((option, index) => (
                    <React.Fragment key={`payment-${index}`}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Nome"
                                fullWidth
                                value={option.name}
                                onChange={(e) => updatePaymentOption(index, 'name', e.target.value)}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Parcelas"
                                fullWidth
                                value={option.installments}
                                onChange={(e) => updatePaymentOption(index, 'installments', e.target.value)}
                                inputMode="numeric"
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <MoneyInput
                                label="Valor"
                                value={option.amount}
                                onChange={(newValue) => updatePaymentOption(index, 'amount', newValue)}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <IconButton
                                onClick={() => setPaymentOptions((prev) => prev.filter((_, i) => i !== index))}
                                disabled={readOnly}
                                aria-label="remover pagamento"
                            >
                                <Delete color="error" />
                            </IconButton>
                        </Grid>
                    </React.Fragment>
                ))}
                {renderFieldError('payment_options')}

                <Grid item xs={12}>
                    <TextField
                        label="Observações"
                        fullWidth
                        multiline
                        minRows={3}
                        value={observations}
                        onChange={(e) => setObservations(e.target.value)}
                        disabled={readOnly}
                    />
                    {renderFieldError('observations')}
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="h4" align="center">
                        Total: {formatCurrencyInCents(amount)}
                    </Typography>
                    {renderFieldError('amount')}
                </Grid>
            </Grid>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 2,
                    marginBottom: 8,
                    gap: 2,
                }}
            >
                {resultErrors.has_errors ? (
                    <div className="text-danger" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                        {resultErrors.form || 'Ocorreram erros de validação, verifique os campos acima.'}
                    </div>
                ) : (
                    <div style={{ height: '1rem' }} />
                )}

                <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isSubmitting || readOnly}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isSubmitting
                        ? 'Salvando...'
                        : mode === 'edit'
                            ? 'Salvar alterações'
                            : 'Criar orçamento'}
                </Button>
            </Box>
        </form>
    );
}

export default QuoteForm;
